import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { Alert, Badge, Button, Col, Row, Space, Tooltip } from 'antd'
import { Data, DetailReq, UpdateReq } from 'Store/delivery-cost/types'
import { selectActionEvent, selectDatas, selectDetailEvent } from 'Store/delivery-cost/selector'
import { selectCurrentUser } from 'Store/user/selector'
import LoadingView from 'Components/modal/LoadingView'
import HomePageLayout from 'Pages/Layout/PageLayout'
import styled from 'styled-components'
import theme, { MyTheme } from 'Components/common/Theme'
import 'antd/es/slider/style'
import { PrimaryButton } from 'Components/button'
import { getDeliveryCostAPI, updateDeliveryCostAPI } from 'Store/delivery-cost/api'
import { mergeArray } from 'helpers/format'
import { clearEventAction } from 'Store/delivery-cost/actions'
import { ConfirmDailog, EventModal } from 'Components/modal'
import DeliveryCostConfig from './DeliveryCostConfig'
import WeightCostConfig from './WeightCostConfig'
import { AlertInfo } from 'Components/alert'

type Props = {} & ReduxProps

const DeliveryCost: React.FunctionComponent<Props> = props => {
  const { currentUser, datas, detailEvent, event } = props
  const dispatch = useDispatch()
  const [distanceList, setDistanceList] = useState([] as Data[])
  const [weightList, setWeightList] = useState([] as Data[])
  const [confirmSaveDialog, setConfirmSaveDialog] = useState(false)

  useEffect(() => {
    const weightData = datas && datas.filter((item: Data) => item.type === 'weight')
    if (weightData) {
      setWeightList(weightData)
    }
    const distanceData = datas && datas.filter((item: Data) => item.type === 'distance')
    if (distanceData) {
      setDistanceList(distanceData)
    }
  }, [datas])

  useEffect(() => {
    onGetDetail()
  }, [])

  const onGetDetail = (): void => {
    const req: DetailReq = {}
    dispatch(getDeliveryCostAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const getDeleteList = () => {
    const deleteList = [] as number[]
    datas?.forEach((originalItem: Data) => {
      const distance = distanceList.find((item: Data) => item.id === originalItem.id)
      const weight = weightList.find((item: Data) => item.id === originalItem.id)
      if (!distance && !weight) {
        deleteList.push(originalItem?.id || 0)
      }
    })
    return deleteList
  }

  const getRequestData = () => {
    const mergeDataList = mergeArray([distanceList, weightList])
    const request = [] as Data[]
    mergeDataList.forEach(item => {
      request.push({
        ...item,
        max_distance: null,
        merchant_id: null,
      })
    })
    return request
  }

  const onSave = () => {
    const req: UpdateReq = {
      delivery_cost_configs: getRequestData(),
      deletes: getDeleteList(),
    }
    dispatch(updateDeliveryCostAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onGetDetail()
    onCloseConfirmDailog()
  }

  const onClickSave = () => {
    onOpenConfirmDailog()
  }

  const onOpenConfirmDailog = () => {
    setConfirmSaveDialog(true)
  }

  const onCloseConfirmDailog = () => {
    setConfirmSaveDialog(false)
  }

  const ConfirmSaveDailog = (
    <ConfirmDailog
      title="ยืนยันการบันทึกข้อมูล"
      description="คุณแน่ใจที่จะบันทึกข้อมูลหรือไม่?"
      confirmBtnTxt="บันทึก"
      isShow={confirmSaveDialog}
      handleOk={onSave}
      handleCancel={onCloseConfirmDailog}
    />
  )

  const onUpdateDistanceConfig = (data: Data[]) => {
    setDistanceList(data)
  }

  const onUpdateWeightConfig = (data: Data[]) => {
    setWeightList(data)
  }

  const validate = (distanceList: Data[]) => {
    let valid = true
    distanceList.forEach(distance => {
      let max = 0
      distance.delivery_cost_configs.forEach(config => {
        if (config.end > max) {
          max = config.end
        }
      })
      if (max < (distance.max_distance || 0)) {
        valid = false
      }
    })
    return valid
  }

  const description = () => {
    return (
      <Space direction="vertical" size="small">
        <Badge
          color={theme.colors.primary}
          text="ระยะการจัดส่งมาตรฐาน 50 กม. คือ ระบบกำหนดให้เป็นค่าเริ่มต้นสำหรับร้าน ร้านค้าจะสามารถจัดส่งได้ในระยะทางไม่เกิน 50 กม. (ร้านค้าสามารถเปลี่ยนแปลงราคาได้)"
        />
        <Badge
          color={theme.colors.primary}
          text="อัตราค่าขนส่งพิเศษ คือ ร้านสามารถเป็นผู้กำหนดระยะทางจัดส่งสูงสุด และกำหนดราคาจัดส่งได้เอง  กรณีมีสินค้าชนิดพิเศษที่ต้องการกำหนดค่าขนส่งที่แตกต่างจากสินค้าชนิดอื่น ๆ"
        />
      </Space>
    )
  }

  return (
    <HomePageLayout
      selected="delivery-costs"
      title="จัดการค่าขนส่งโดยร้านค้า"
      label={'เลือกจัดการค่าขนส่งสำหรับร้านค้าจัดส่งเอง'}
    >
      <Description>
        <AlertInfo
          message="ร้านค้าสามารถกำหนดวิธีการจัดส่งสินค้าเองได้ดังนี้"
          description={description()}
          banner
          closable
        />
      </Description>
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <BoxWrapper>
            <DeliveryCostConfig
              distanceDataList={distanceList}
              onUpdate={onUpdateDistanceConfig}
              mid={currentUser?.activeMerchant?.id || 0}
            />
          </BoxWrapper>
        </Col>
        <Col span={24}>
          <BoxWrapper>
            <WeightCostConfig weightDataList={weightList} onUpdate={onUpdateWeightConfig} />
          </BoxWrapper>
        </Col>
      </Row>
      <br />
      <Row justify="center">
        <Col>
          <Space size="large">
            <Tooltip
              placement="top"
              title={
                !validate(distanceList)
                  ? 'กรุณาเพิ่มเงื่อนไขคำนวนค่าขนส่งให้ครบตามระยะทางสูงสุดที่ระบุไว้ ก่อนบันทึกข้อมูล'
                  : ''
              }
            >
              <MyButton type="primary" onClick={onClickSave} disabled={!validate(distanceList)}>
                บันทึกข้อมูล
              </MyButton>
            </Tooltip>

            <PrimaryButton btnType="Cancel" text="ยกเลิก" onClick={onGetDetail} />
          </Space>
        </Col>
      </Row>

      {ConfirmSaveDailog}
    </HomePageLayout>
  )
}

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  margin-bottom: 10px;
  background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.white};

  .ant-form-item {
    margin-bottom: 0 !important;
  }
`

const MyButton = styled(Button)`
  height: 45px;
  border-radius: 5px;
`

const Description = styled.div`
  .ant-descriptions-title {
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  }
 //background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.white};
  padding: 5px 0;
  margin: 10px 0;
  th,
  td,
  span {
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  }
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectActionEvent(state),
    datas: selectDatas(state),
    detailEvent: selectDetailEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(DeliveryCost)
